/**
 * Growth Engine roles (ADR-0016). Unlike the WanderFitness backoffice, these
 * come from OUR DB via `GET /api/auth/me`, not from Firebase custom claims:
 *   - SUPERADMIN   platform-level (User.superadmin), passes every role gate.
 *   - TENANT_ADMIN the client/domain admin — manages their tenant's data + users.
 *   - MARKETING    runs campaigns within a tenant.
 *   - REP          sales rep (ADR-0027) — only sees/completes their own manual tasks.
 */
export const Role = {
  SUPERADMIN: "SUPERADMIN",
  TENANT_ADMIN: "TENANT_ADMIN",
  MARKETING: "MARKETING",
  REP: "REP",
} as const;

export type Role = (typeof Role)[keyof typeof Role];

/** Roles allowed into the app shell — every authenticated Growth user (incl. a REP). */
export const APP_ROLES = [Role.SUPERADMIN, Role.TENANT_ADMIN, Role.MARKETING, Role.REP] as const;

/** Tenant-management surfaces (users, settings) — admins only. */
export const ADMIN_ROLES = [Role.SUPERADMIN, Role.TENANT_ADMIN] as const;

/** Full-app roles (everything except a task-only REP) — for screens a REP must not reach. */
export const NON_REP_ROLES = [Role.SUPERADMIN, Role.TENANT_ADMIN, Role.MARKETING] as const;

const ALL_ROLES = new Set<string>(Object.values(Role));

/** Whitelist a value against the Role enum; anything else becomes null. */
export function parseRole(value: unknown): Role | null {
  if (typeof value !== "string") return null;
  return ALL_ROLES.has(value) ? (value as Role) : null;
}
